/**
 * Storage keys used for authentication state
 */
const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  MFA_EMAIL: 'mfa_email',
  MFA_TOKEN: 'mfa_token',
  MFA_REMEMBER_ME: 'mfa_remember_me',
  MFA_ACTIVATION_KEY: 'mfa_activation_key_from_login',
} as const;

/**
 * Clears all authentication state from localStorage.
 * Safe to call multiple times (idempotent).
 */
export function clearAuthState(): void {
  // Clear all localStorage tokens
  Object.values(AUTH_STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
}

/**
 * BroadcastChannel for cross-tab logout synchronization.
 * Falls back gracefully if BroadcastChannel is not supported.
 */
const LOGOUT_CHANNEL_NAME = 'tapt_logout_channel';

let logoutChannel: BroadcastChannel | null = null;

export function getLogoutChannel(): BroadcastChannel | null {
  if (logoutChannel) return logoutChannel;

  try {
    logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    return logoutChannel;
  } catch (e) {
    // BroadcastChannel not supported (e.g., older Safari)
    console.warn('BroadcastChannel not supported, multi-tab logout disabled');
    return null;
  }
}

/**
 * Broadcasts logout event to other tabs
 */
export function broadcastLogout(): void {
  const channel = getLogoutChannel();
  channel?.postMessage({ type: 'LOGOUT' });
}

/**
 * Subscribes to logout events from other tabs.
 * Returns cleanup function.
 */
export function onLogoutBroadcast(callback: () => void): () => void {
  const channel = getLogoutChannel();

  if (!channel) {
    return () => {}; // No-op cleanup
  }

  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'LOGOUT') {
      callback();
    }
  };

  channel.addEventListener('message', handler);
  return () => channel.removeEventListener('message', handler);
}
